import { IconLoader2 } from "@tabler/icons-react"
import { type FC, useState } from "react"

import { type DomainPeriodOption } from "@forento/core/models/domain"
import { toPriceStringUSD } from "@forento/core/utilities/currency"
import { toDateString } from "@forento/core/utilities/date"
import { cn } from "@forento/ui/utilities/tailwind"

import Button, { actionClass } from "~/components/Button"
import Dropdown from "~/components/Dropdown"
import Link from "~/components/Link"
import { DefaultModal, ModalButtons, ModalInputs, ModalText, ModalTitle } from "~/components/Modal"
import PartialLoadingPage from "~/components/PartialLoadingPage"
import { useAlert } from "~/contexts/AlertContext"
import routes from "~/utilities/routes"
import trpc from "~/utilities/trpc"

import DomainHeading from "../DomainHeading"

type Props = {
	domain: {
		id: number
		domain: string
		isSelected: boolean
		platform: { subdomain: string }
		expireDate: Date
	}
	reload(): Promise<void>
}
const AutomaticDomain: FC<Props> = ({ domain, reload }) => {
	const alert = useAlert()
	const isExpired = domain.expireDate <= new Date()

	const [isSelecting, setSelecting] = useState(false)
	const [renewal, setRenewal] = useState<{
		isOpen: boolean
		isLoadingOptions: boolean
		isSubmitting: boolean
		options: DomainPeriodOption[]
		years: number | null
		error: string | null
	}>({ isOpen: false, isLoadingOptions: false, isSubmitting: false, options: [], years: null, error: null })

	async function handleSetSelected(selected: boolean) {
		setSelecting(true)
		try {
			await trpc.domain.setSelected.mutate({ domain: selected ? domain.domain : null })
			await reload()
		} catch (error) {
			console.error(`Failed to ${selected ? "select" : "deselect"} domain!`, error)
			setSelecting(false)
		}
		setSelecting(false)
	}

	async function handleChangeSubdomain() {
		const result = await alert.input(
			"Change subdomain",
			`Enter a new subdomain for the domain **${domain.domain}**`,
			"Subdomain",
			domain.platform.subdomain,
		)
		if (result.result === null) return

		await trpc.domain.updateAutomatic.mutate({ id: domain.id, data: { platformSubdomain: result.result.trim() } })
		await reload()

		result.close()
	}

	async function handleOpenRenewal() {
		setRenewal({
			isOpen: true,
			isLoadingOptions: true,
			isSubmitting: false,
			options: [],
			years: null,
			error: null,
		})
		try {
			const response = await trpc.domain.listRenewalOptions.query(domain.id)
			if (response.status === "unavailable") {
				setRenewal(current => ({
					...current,
					isLoadingOptions: false,
					isSubmitting: false,
					options: [],
					years: null,
					error: "This domain cannot be renewed online. Contact support for help recovering it.",
				}))
				return
			}
			if (response.status !== "success") {
				setRenewal(current => ({
					...current,
					isLoadingOptions: false,
					error: "Failed to load renewal options. Please try again later.",
				}))
				return
			}
			setRenewal(current => ({
				...current,
				isLoadingOptions: false,
				options: response.options,
				years: response.options[0]?.years ?? null,
			}))
		} catch (error) {
			console.error("Failed to load domain renewal options!", error)
			setRenewal(current => ({
				...current,
				isLoadingOptions: false,
				error: "Failed to load renewal options. Please try again later.",
			}))
		}
	}

	async function handleRenew() {
		if (renewal.years === null) return

		setRenewal(current => ({ ...current, isSubmitting: true, error: null }))
		try {
			const response = await trpc.domain.renew.mutate({ id: domain.id, years: renewal.years })
			if (response.status === "unavailable") {
				setRenewal(current => ({
					...current,
					isLoadingOptions: false,
					isSubmitting: false,
					options: [],
					years: null,
					error: "This domain cannot be renewed online. Contact support for help recovering it.",
				}))
				return
			}
			if (response.status === "failed") {
				setRenewal(current => ({
					...current,
					isSubmitting: false,
					error: "Failed to start renewal. Please try again later.",
				}))
				return
			}
			if (response.status === "unsupported-period") {
				setRenewal(current => ({
					...current,
					isSubmitting: false,
					error: "The selected renewal period is no longer available for this domain.",
				}))
				return
			}
			if (response.status !== "success") return
			window.location.assign(response.redirectUrl)
		} catch (error) {
			console.error("Failed to start domain renewal!", error)
			setRenewal(current => ({
				...current,
				isSubmitting: false,
				error: "Failed to start renewal. Please try again later.",
			}))
		}
	}

	const selectedRenewalOption = renewal.options.find(option => option.years === renewal.years) ?? null

	const closeRenewal = () =>
		setRenewal({
			isOpen: false,
			isLoadingOptions: false,
			isSubmitting: false,
			options: [],
			years: null,
			error: null,
		})

	return (
		<>
			<DefaultModal isOpen={renewal.isOpen} onClose={renewal.isSubmitting ? undefined : closeRenewal}>
				<ModalTitle>Renew domain</ModalTitle>
				{renewal.isLoadingOptions ? (
					<PartialLoadingPage />
				) : (
					<>
						<ModalText>
							Renew <strong>{domain.domain}</strong> with a one-off payment.
						</ModalText>
						{renewal.error !== null && <ModalText>{renewal.error}</ModalText>}
						{renewal.options.length > 0 && renewal.years !== null && (
							<ModalInputs>
								<Dropdown
									label="Renewal period"
									items={renewal.options.map(option => ({
										id: option.years.toString(),
										title: `${option.years} ${option.years === 1 ? "year" : "years"} - ${toPriceStringUSD(option.price)}`,
									}))}
									selectedItemId={renewal.years.toString()}
									onChange={years => setRenewal(current => ({ ...current, years: parseInt(years) }))}
								/>
								{selectedRenewalOption !== null && (
									<div className="font-semibold">{toPriceStringUSD(selectedRenewalOption.price)}</div>
								)}
							</ModalInputs>
						)}
						<ModalButtons>
							<Button variant="secondary" onClick={closeRenewal} isDisabled={renewal.isSubmitting}>
								Cancel
							</Button>
							<Button
								variant="primary"
								onClick={handleRenew}
								isLoading={renewal.isSubmitting}
								isDisabled={renewal.years === null || renewal.options.length === 0}
							>
								Continue to payment
							</Button>
						</ModalButtons>
					</>
				)}
			</DefaultModal>
			<section className="border-component-border overflow-hidden rounded-2xl border bg-white">
				<div className="flex flex-wrap items-start justify-between gap-5 p-4 sm:p-5">
					<DomainHeading
						domain={domain.domain}
						subdomain={domain.platform.subdomain}
						isSelected={domain.isSelected}
						description="Registered with Forento"
						status={
							isExpired ? (
								<span className="bg-danger/10 text-danger rounded px-2 py-1 text-xs font-medium">
									Expired
								</span>
							) : undefined
						}
					/>
					<div className="text-sm">
						<p className="text-xs text-gray-500">{isExpired ? "Expired on" : "Expires on"}</p>
						<p className="mt-1 font-medium">{toDateString(domain.expireDate)}</p>
					</div>
				</div>
				{isExpired && (
					<p className="text-danger px-5 pb-4 text-sm">
						Renew this domain to restore access to your academy.
					</p>
				)}
				<div className="border-component-border bg-component-background/40 flex flex-wrap gap-2 border-t px-4 py-3 sm:px-5">
					<button type="button" onClick={handleOpenRenewal} className={actionClass}>
						Renew
					</button>
					{!isExpired && (
						<>
							<Link to={routes.settings.domain.manageDns(domain.id)} className={actionClass}>
								Manage DNS
							</Link>
							<button type="button" onClick={handleChangeSubdomain} className={actionClass}>
								Change subdomain
							</button>
							{domain.isSelected ? (
								<button
									type="button"
									onClick={() => handleSetSelected(false)}
									disabled={isSelecting}
									className={cn(actionClass, "text-danger")}
								>
									{isSelecting && <IconLoader2 className="size-3 animate-spin" />}
									Disconnect
								</button>
							) : (
								<button
									type="button"
									onClick={() => handleSetSelected(true)}
									disabled={isSelecting}
									className={cn(actionClass, "border-primary/20 text-primary")}
								>
									{isSelecting && <IconLoader2 className="size-3 animate-spin" />}
									Publish
								</button>
							)}
						</>
					)}
				</div>
			</section>
		</>
	)
}

export default AutomaticDomain
